import type { ColumnsType } from 'antd/es/table';
import type { MeetingData, MeetingType } from '../types';

import { Dispatch, SetStateAction } from 'react';
import { TFunction } from 'i18next';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { Flex } from 'antd';
import dayjs from 'dayjs';

import withLoadingSkeleton from '@/HOCs/withLoadingSkeleton';
import StyledSearchOutlined from '@/components/Shared/StyledSearchOutlined';
import TablesSearchDropdown from '@/features/captable/components/Shared/TablesSearchDropdown';
import { StyledButton } from '@/lib/theme/components/Button';
import themeToken from '@lib/theme/tokens/index';
import EditIcon from '@/components/Icons/EditIcon';
import DeleteIcon from '@/components/Icons/DeleteIcon';

import { MeetingStatus } from './enums';
import MeetingStatusTag from '../components/MeetingStatusTag';

export const meetingsColumns = (
  setSearchToken: Dispatch<SetStateAction<string | undefined>>,
  onEdit: (meetingId: number) => void,
  onDelete: (meetingId: number, meetingName: string) => void,
  isLoading: boolean,
  isDeleteBtnDisabled: boolean,
  meetingTypes: MeetingType[] | undefined,
  isCompanyOwner: boolean | undefined,
  t: TFunction<'investorRelations', 'meetings'>,
): ColumnsType<MeetingData | Record<string, number>> => [
  {
    title: t('name'),
    dataIndex: 'name',
    render: withLoadingSkeleton(isLoading, (val: string) => val),
    sorter: true,
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: (keys: React.Key[]) => void;
      selectedKeys: React.Key[];
      confirm: FilterDropdownProps['confirm'];
      close: () => void;
      clearFilters?: () => void;
    }) => (
      <TablesSearchDropdown
        dataIndex="equity_class"
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearFilters}
        confirm={confirm}
        close={close}
        setSearchToken={setSearchToken}
      />
    ),
    filterIcon: () => <StyledSearchOutlined />,
  },
  {
    title: t('date'),
    dataIndex: 'start_time',
    sorter: true,
    render: withLoadingSkeleton(isLoading, (val: Date) => dayjs(val).format('DD MMMM YYYY, h A')),
  },
  {
    title: t('type'),
    dataIndex: 'meeting_type_id',
    sorter: true,
    render: withLoadingSkeleton(isLoading, (val: number) => {
      const currMeetingType = meetingTypes?.find((meetingType) => meetingType.id === val);

      return currMeetingType?.name || '-';
    }),
  },
  {
    title: t('status'),
    dataIndex: 'status',
    sorter: true,
    render: withLoadingSkeleton(isLoading, (val: MeetingStatus) => <MeetingStatusTag status={val} tagText={val} />),
  },

  {
    title: t('actions'),
    width: '180px',
    hidden: !isCompanyOwner,
    render: withLoadingSkeleton(isLoading, (_, record) => {
      let actionsCount = 1;
      if (record?.status === MeetingStatus.DRAFTED) {
        actionsCount = 2;
      }

      return (
        <Flex
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {actionsCount === 2 && (
            <StyledButton
              style={{
                padding: `${(themeToken.paddingXXS, themeToken.paddingXS)}`,
                color: themeToken.colorPrimary,
              }}
              size="small"
              type="link"
              icon={<EditIcon stroke={themeToken.colorPrimary} />}
              onClick={() => {
                onEdit(record?.id as number);
              }}
            >
              {t('edit')}
            </StyledButton>
          )}
          <StyledButton
            style={{
              padding: `${(themeToken.paddingXXS, themeToken.paddingXS)}`,
              color: themeToken.colorPrimary,
            }}
            size="small"
            type="link"
            icon={<DeleteIcon />}
            onClick={() => {
              onDelete(record?.id as number, record?.name as string);
            }}
            disabled={isDeleteBtnDisabled || record?.status !== MeetingStatus.DRAFTED}
          >
            {t('delete')}
          </StyledButton>
        </Flex>
      );
    }),
  },
];
